import React, { useMemo } from "react";
import {
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { contrastingStrokeForFill, getColorForDate } from "../colors";
import { formatTick, formatLengthTick as formatLengthTickUtil } from "../formatters";

const SCATTER_SHAPES = [
  "circle",
  "cross",
  "diamond",
  "square",
  "star",
  "triangle",
  "wye",
];

const SHAPE_LABELS = {
  circle: "Circle",
  cross: "Cross",
  diamond: "Diamond",
  square: "Square",
  star: "Star",
  triangle: "Triangle",
  wye: "Wye",
};

function getShapeForSpecies(species, speciesList) {
  const idx = speciesList.indexOf(species);
  return SCATTER_SHAPES[(idx >= 0 ? idx : 0) % SCATTER_SHAPES.length];
}

const TAG_TRAJECTORY_COLORS = [
  "#1d4ed8",
  "#7c3aed",
  "#be185d",
  "#b45309",
  "#0f766e",
  "#374151",
  "#166534",
];

function isTagSeriesName(name) {
  return /^Tag\s+/i.test(String(name || "").trim());
}

function getTagTrajectoryColor(seriesName, speciesList) {
  const idx = speciesList.indexOf(seriesName);
  const safeIdx = idx >= 0 ? idx : 0;
  return TAG_TRAJECTORY_COLORS[safeIdx % TAG_TRAJECTORY_COLORS.length];
}

export default function WeightVsLengthByDateChart({
  graphData,
  selectedUnit,
  selectedFish,
  isMobile,
  hoveredWeightPoint,
  setHoveredWeightPoint,
}) {
  const formatLengthTick = (v) => formatLengthTickUtil(v, selectedUnit.value);

  const { sortedDateKeys, dateKeyToLabel } = useMemo(() => {
    const keys = [...new Set(graphData.map((d) => d.sampleDateKey || "unknown"))];
    keys.sort((a, b) => a.localeCompare(b));
    const labelByKey = {};
    graphData.forEach((d) => {
      const k = d.sampleDateKey || "unknown";
      if (labelByKey[k] == null && d.sampleDateLabel != null) {
        labelByKey[k] = d.sampleDateLabel;
      }
    });
    keys.forEach((k) => {
      if (labelByKey[k] == null) labelByKey[k] = k;
    });
    return { sortedDateKeys: keys, dateKeyToLabel: labelByKey };
  }, [graphData]);

  const speciesOrder = useMemo(
    () => [...new Set(graphData.map((d) => d.species))],
    [graphData]
  );

  const commonProps = {
    width: "100%",
    height: isMobile ? 500 : 700,
    data: graphData,
    margin: {
      top: isMobile ? 20 : 40,
      right: isMobile ? 20 : 60,
      left: isMobile ? 20 : 80,
      bottom: isMobile ? 80 : 120,
    },
  };

  const uniqueSpeciesWeight = [...new Set(graphData.map((d) => d.species))];

  const xValsW = graphData.map((d) => Number(d.length || 0));
  const yValsW = graphData.map((d) =>
    Number(Math.max(d.actualWeight || 0, d.standardWeight || 0))
  );

  const isEnglishUnits = selectedUnit.value === "English";
  const targetTickCountWeight = 20;

  let xStepW;
  let xTicksW = [];
  let yStepW;
  let yTicksW = [];

  const xMinW = xValsW.length ? Math.min(...xValsW) : 0;
  const xMaxW = xValsW.length ? Math.max(...xValsW) : 0;

  if (isEnglishUnits) {
    const xEndW = xMaxW + 1;
    const rangeForStepW = xEndW - Math.max(0, Math.floor(xMinW) - 1);
    const xRangeW = rangeForStepW;

    if (xRangeW < 4) {
      xStepW = 0.1;
    } else if (xRangeW < 8) {
      xStepW = 0.2;
    } else if (xRangeW < 12) {
      xStepW = 0.3;
    } else if (xRangeW < 16) {
      xStepW = 0.4;
    } else if (xRangeW < 20) {
      xStepW = 0.5;
    } else if (xRangeW < 24) {
      xStepW = 0.6;
    } else if (xRangeW < 28) {
      xStepW = 0.7;
    } else if (xRangeW < 32) {
      xStepW = 0.8;
    } else if (xRangeW < 36) {
      xStepW = 0.9;
    } else {
      xStepW = 1.0;
    }

    const xStartW = Math.max(0, xMinW - xStepW);

    xTicksW = [];
    for (let v = xStartW; v <= xEndW + 1e-9; v += xStepW) {
      xTicksW.push(Number(v.toFixed(2)));
    }

    while (xTicksW.length < targetTickCountWeight) {
      const lastTick = xTicksW[xTicksW.length - 1];
      xTicksW.push(Number((lastTick + xStepW).toFixed(2)));
    }
  } else {
    const xEndW = xMaxW + 100;
    const rangeForStepW = xEndW - Math.max(0, xMinW - 100);
    const xRangeW = rangeForStepW;

    if (xRangeW < 400) {
      xStepW = 10;
    } else if (xRangeW < 600) {
      xStepW = 15;
    } else if (xRangeW < 800) {
      xStepW = 20;
    } else if (xRangeW < 1000) {
      xStepW = 25;
    } else if (xRangeW < 1200) {
      xStepW = 30;
    } else if (xRangeW < 1400) {
      xStepW = 35;
    } else if (xRangeW < 1600) {
      xStepW = 40;
    } else if (xRangeW < 1800) {
      xStepW = 45;
    } else if (xRangeW < 2000) {
      xStepW = 50;
    } else if (xRangeW < 2200) {
      xStepW = 55;
    } else if (xRangeW < 2400) {
      xStepW = 60;
    } else if (xRangeW < 2600) {
      xStepW = 65;
    } else if (xRangeW < 2800) {
      xStepW = 70;
    } else if (xRangeW < 3000) {
      xStepW = 75;
    } else if (xRangeW < 3200) {
      xStepW = 80;
    } else if (xRangeW < 3400) {
      xStepW = 85;
    } else if (xRangeW < 3600) {
      xStepW = 90;
    } else if (xRangeW < 3800) {
      xStepW = 95;
    } else if (xRangeW < 4000) {
      xStepW = 100;
    } else {
      xStepW = 200;
    }

    const xStartW = Math.max(0, xMinW - xStepW);

    xTicksW = [];
    for (let v = xStartW; v <= xEndW + 1e-9; v += xStepW) {
      xTicksW.push(Number(v.toFixed(0)));
    }

    while (xTicksW.length < targetTickCountWeight) {
      const lastTick = xTicksW[xTicksW.length - 1];
      xTicksW.push(Number(lastTick + xStepW));
    }
  }

  const yMaxW = yValsW.length ? Math.max(...yValsW) : 0;

  if (isEnglishUnits) {
    if (yMaxW < 1.5) {
      yStepW = 0.05;
    } else if (yMaxW < 2) {
      yStepW = 0.1;
    } else if (yMaxW < 4) {
      yStepW = 0.2;
    } else if (yMaxW < 8) {
      yStepW = 0.4;
    } else if (yMaxW < 12) {
      yStepW = 0.6;
    } else if (yMaxW < 16) {
      yStepW = 0.8;
    } else if (yMaxW < 20) {
      yStepW = 1.0;
    } else if (yMaxW < 24) {
      yStepW = 1.2;
    } else if (yMaxW < 28) {
      yStepW = 1.4;
    } else if (yMaxW < 32) {
      yStepW = 1.6;
    } else if (yMaxW < 36) {
      yStepW = 1.8;
    } else {
      yStepW = 2.0;
    }

    const requiredRangeY = (targetTickCountWeight - 1) * yStepW;
    const actualRangeY = Math.max(yMaxW, requiredRangeY);
    const yUpperW = Math.ceil(actualRangeY / yStepW) * yStepW;

    yTicksW = [];
    for (let v = 0; v <= yUpperW + 1e-9; v += yStepW) {
      yTicksW.push(Number(v.toFixed(2)));
    }

    while (yTicksW.length < targetTickCountWeight) {
      const lastTick = yTicksW[yTicksW.length - 1];
      yTicksW.push(Number((lastTick + yStepW).toFixed(2)));
    }
  } else {
    const range = yMaxW;
    if (range < 200) {
      yStepW = 10;
    } else if (range < 400) {
      yStepW = 20;
    } else if (range < 600) {
      yStepW = 30;
    } else if (range < 800) {
      yStepW = 40;
    } else if (range < 1000) {
      yStepW = 50;
    } else if (range < 1200) {
      yStepW = 60;
    } else if (range < 1400) {
      yStepW = 70;
    } else if (range < 1600) {
      yStepW = 80;
    } else if (range < 1800) {
      yStepW = 90;
    } else if (range < 2000) {
      yStepW = 100;
    } else if (range < 2200) {
      yStepW = 110;
    } else if (range < 2400) {
      yStepW = 120;
    } else if (range < 2600) {
      yStepW = 130;
    } else if (range < 2800) {
      yStepW = 140;
    } else if (range < 3000) {
      yStepW = 150;
    } else if (range < 3200) {
      yStepW = 160;
    } else if (range < 3400) {
      yStepW = 170;
    } else if (range < 3600) {
      yStepW = 180;
    } else if (range < 3800) {
      yStepW = 190;
    } else if (range < 4000) {
      yStepW = 200;
    } else {
      yStepW = 200;
    }

    const requiredRangeY = (targetTickCountWeight - 1) * yStepW;
    const actualRangeY = Math.max(yMaxW, requiredRangeY);
    const yUpperW = Math.ceil(actualRangeY / yStepW) * yStepW;

    yTicksW = [];
    for (let v = 0; v <= yUpperW + 1e-9; v += yStepW) {
      yTicksW.push(Number(v.toFixed(0)));
    }

    while (yTicksW.length < targetTickCountWeight) {
      const lastTick = yTicksW[yTicksW.length - 1];
      yTicksW.push(Number(lastTick + yStepW));
    }
  }

  const renderScatterSeries = (species, keyPrefix) => {
    const speciesData = graphData.filter((d) => d.species === species);
    const samp = speciesData.filter((d) => !d.isStocking);
    const stock = speciesData.filter((d) => d.isStocking);
    const shapeName = getShapeForSpecies(species, speciesOrder);
    const isTagSeries = isTagSeriesName(species);
    const sortedByDate = [...samp].sort((a, b) => {
      const da = String(a.sampleDateKey || "");
      const db = String(b.sampleDateKey || "");
      if (da === db) return Number(a.length || 0) - Number(b.length || 0);
      return da.localeCompare(db);
    });
    return (
      <React.Fragment key={keyPrefix}>
        {samp.length > 0 && (
          <Scatter
            name={`${species} (${samp.length})`}
            data={samp}
            dataKey="actualWeight"
            shape={shapeName}
            fill="#888888"
            fillOpacity={0.85}
            legendType="none"
            onMouseEnter={(o) => setHoveredWeightPoint(o?.payload || null)}
            onMouseLeave={() => setHoveredWeightPoint(null)}
          >
            {samp.map((entry, index) => {
              const fill = getColorForDate(
                entry.sampleDateKey || "unknown",
                sortedDateKeys
              );
              return (
                <Cell
                  key={`cell-${keyPrefix}-${index}`}
                  fill={fill}
                  stroke={contrastingStrokeForFill(fill)}
                  strokeWidth={2}
                />
              );
            })}
          </Scatter>
        )}
        {stock.length > 0 && (
          <Scatter
            name={`${species} (stocking) (${stock.length})`}
            data={stock}
            dataKey="actualWeight"
            shape="diamond"
            fill="#888888"
            fillOpacity={0.95}
            legendType="none"
            onMouseEnter={(o) => setHoveredWeightPoint(o?.payload || null)}
            onMouseLeave={() => setHoveredWeightPoint(null)}
          >
            {stock.map((entry, index) => {
              const fill = getColorForDate(
                entry.sampleDateKey || "unknown",
                sortedDateKeys
              );
              return (
                <Cell
                  key={`cell-stk-${keyPrefix}-${index}`}
                  fill={fill}
                  stroke={contrastingStrokeForFill(fill)}
                  strokeWidth={2}
                />
              );
            })}
          </Scatter>
        )}
        {isTagSeries && sortedByDate.length > 1 && (
          <Line
            name={`${species} Actual growth`}
            data={sortedByDate}
            dataKey="actualWeight"
            stroke={getTagTrajectoryColor(species, speciesOrder)}
            strokeWidth={2.5}
            dot={false}
            type="linear"
            legendType="line"
            connectNulls
          />
        )}
        {samp.length > 0 && (
          <Line
            name={`${species} Standard (Ws)`}
            data={[...samp].sort((a, b) => a.length - b.length)}
            dataKey="standardWeight"
            stroke="#6b7280"
            strokeWidth={2}
            strokeDasharray="6 4"
            dot={false}
            type="monotone"
            legendType="line"
          />
        )}
      </React.Fragment>
    );
  };

  return (
    <ResponsiveContainer {...commonProps}>
      <ComposedChart data={graphData}>
        <CartesianGrid strokeDasharray="3 3" stroke="#000000" strokeWidth={1} />
        <XAxis
          dataKey="length"
          label={{
            value: `Length (${selectedUnit.value === "English" ? "Inch" : "mm"})`,
            position: "outsideBottom",
            offset: isMobile ? 10 : 20,
            fontSize: isMobile ? 14 : 18,
            fontWeight: "bold",
            fill: "#000000",
          }}
          type="number"
          domain={[xTicksW[0] || 0, xTicksW[xTicksW.length - 1] || 0]}
          ticks={xTicksW}
          tickFormatter={formatLengthTick}
          angle={isMobile ? -45 : 0}
          textAnchor="middle"
          height={isMobile ? 80 : 60}
          tick={{
            fontSize: isMobile ? 10 : 12,
            fontWeight: "bold",
            fill: "#000000",
          }}
          stroke="#000000"
          strokeWidth={2}
          interval={0}
          allowDataOverflow={true}
          padding={{ right: 10 }}
        />
        <YAxis
          label={{
            value: `Weight (${selectedUnit.value === "English" ? "Pound" : "gram"})`,
            angle: -90,
            position: "insideLeft",
            style: { textAnchor: "middle" },
            fontSize: isMobile ? 14 : 18,
            fontWeight: "bold",
            fill: "#000000",
          }}
          domain={[0, yTicksW[yTicksW.length - 1] || 0]}
          ticks={yTicksW}
          tickFormatter={(value) => {
            if (selectedUnit.value === "English") {
              return formatTick(value);
            }
            return Math.round(value).toString();
          }}
          tick={{
            fontSize: isMobile ? 10 : 12,
            fontWeight: "bold",
            fill: "#000000",
          }}
          stroke="#000000"
          strokeWidth={2}
          interval={0}
        />
        <Tooltip
          filterNull
          isAnimationActive={false}
          cursor={{ stroke: "transparent" }}
          content={({ active, payload }) => {
            if (!hoveredWeightPoint) return null;
            if (!active || !payload || !payload.length) return null;

            const chosen =
              payload.find(
                (p) => p.dataKey === "actualWeight" && p.payload === hoveredWeightPoint
              ) || payload[0];
            const data = chosen.payload;
            const color =
              getColorForDate(
                data.sampleDateKey || "unknown",
                sortedDateKeys
              ) || "#333";
            const unit = selectedUnit.value === "English" ? "Pound" : "gram";
            const lengthUnit = selectedUnit.value === "English" ? "Inch" : "mm";
            const weightDiff = (data.actualWeight - data.standardWeight).toFixed(2);
            const sw = Number(data.standardWeight) || 0;
            const percentDiff =
              sw > 0
                ? (((data.actualWeight / sw - 1) * 100).toFixed(1))
                : "0.0";

            return (
              <div
                className="custom-tooltip"
                style={{
                  background: "#fff",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  padding: "12px",
                  fontSize: isMobile ? "12px" : "14px",
                  minWidth: "220px",
                }}
              >
                <div
                  style={{
                    borderBottom: "2px solid " + color,
                    marginBottom: "8px",
                    paddingBottom: "4px",
                  }}
                >
                  <p style={{ margin: 0, fontWeight: "bold", color }}>
                    {data.species}
                    {data.isStocking ? " · Stocking" : ""}
                  </p>
                </div>
                <div style={{ display: "grid", gap: "6px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <strong>{data.isStocking ? "Stocking date:" : "Sampling date:"}</strong>
                    <span>{data.sampleDateLabel ?? data.sampleDateKey ?? "—"}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <strong>Length:</strong>
                    <span>
                      {data.length.toFixed(2)} {lengthUnit}
                    </span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <strong>Actual Weight:</strong>
                    <span style={{ fontWeight: "bold" }}>
                      {data.actualWeight.toFixed(2)} {unit}
                    </span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <strong>Standard Weight:</strong>
                    <span>
                      {data.standardWeight.toFixed(2)} {unit}
                    </span>
                  </div>
                  <div
                    style={{
                      marginTop: "4px",
                      padding: "8px",
                      backgroundColor: "#f8f9fa",
                      borderRadius: "3px",
                      fontSize: isMobile ? "11px" : "13px",
                    }}
                  >
                    <div
                      style={{
                        color:
                          data.actualWeight - data.standardWeight >= 0
                            ? "#27ae60"
                            : "#c0392b",
                        marginBottom: "4px",
                      }}
                    >
                      <strong>Difference:</strong> {weightDiff > 0 ? "+" : ""}
                      {weightDiff} {unit}
                    </div>
                    <div
                      style={{
                        color:
                          sw > 0 &&
                          (data.actualWeight / sw - 1) * 100 >= 0
                            ? "#27ae60"
                            : "#c0392b",
                      }}
                    >
                      <strong>Percentage:</strong> {percentDiff > 0 ? "+" : ""}
                      {percentDiff}%
                    </div>
                  </div>
                </div>
              </div>
            );
          }}
        />
        <Legend
          verticalAlign="top"
          align="center"
          content={() => (
            <div
              style={{
                paddingBottom: isMobile ? 12 : 20,
                fontSize: isMobile ? 11 : 13,
                maxHeight: isMobile ? 140 : 180,
                overflowY: "auto",
                width: "100%",
              }}
            >
              <div
                style={{
                  fontWeight: "bold",
                  marginBottom: 6,
                  color: "#111827",
                }}
              >
                Sampling date (point color)
              </div>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px 16px",
                  marginBottom: 12,
                }}
              >
                {sortedDateKeys.map((dk) => (
                  <div
                    key={dk}
                    style={{ display: "flex", alignItems: "center", gap: 6 }}
                  >
                    <span
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: 2,
                        backgroundColor: getColorForDate(dk, sortedDateKeys),
                        flexShrink: 0,
                      }}
                    />
                    <span>{dateKeyToLabel[dk] ?? dk}</span>
                  </div>
                ))}
              </div>
              <div
                style={{
                  fontWeight: "bold",
                  marginBottom: 6,
                  color: "#111827",
                }}
              >
                Species (point shape)
              </div>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px 16px",
                }}
              >
                {speciesOrder.map((sp) => {
                  const sh = getShapeForSpecies(sp, speciesOrder);
                  return (
                    <div
                      key={sp}
                      style={{ display: "flex", alignItems: "center", gap: 6 }}
                    >
                      <span style={{ color: "#4b5563", minWidth: 72 }}>
                        {SHAPE_LABELS[sh] ?? sh}:
                      </span>
                      <span>{sp}</span>
                    </div>
                  );
                })}
              </div>
              <div
                style={{
                  marginTop: 10,
                  fontSize: isMobile ? 10 : 12,
                  color: "#6b7280",
                }}
              >
                Gray dashed lines = standard weight (Ws) by species.
                <br />
                In Individual Tags mode, solid colored lines connect each tag's
                actual measurements by sampling date.
              </div>
            </div>
          )}
        />
        {uniqueSpeciesWeight.map((species) =>
          renderScatterSeries(species, species)
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
}
